// Tote labels — Warehouse › Totes print.
//
// One design: CODE128 bars across the full width on top, the tote ID
// underneath in the biggest type that fits, so a picker can read "5023" from
// across an aisle and the scanner still gets tall bars.
//
// The stock size is remembered PER DEVICE (localStorage), not per warehouse:
// totes are one global pool with no warehouse of their own, so there's no
// warehouse setting to hang it on — and whoever prints totes does it from the
// desk the label printer is plugged into.

import type { BayLabel } from "../SortingLabelPrintModal";

/** Stock the tote label is designed on when nothing is saved. */
export const TOTE_LABEL_BASE = { widthMm: 100, heightMm: 50 } as const;

const SIZE_KEY = "toteLabelSize";

type Mm = { widthMm: number; heightMm: number };

/** The saved size, or nulls (= TOTE_LABEL_BASE) when there's nothing usable. */
export function readToteLabelSize(): { widthMm: number | null; heightMm: number | null } {
    try {
        const raw = JSON.parse(localStorage.getItem(SIZE_KEY) ?? "null");
        const ok = (n: unknown) => typeof n === "number" && n >= 20 && n <= 300 ? n : null;
        return { widthMm: ok(raw?.widthMm), heightMm: ok(raw?.heightMm) };
    } catch {
        return { widthMm: null, heightMm: null };
    }
}

/** Persist the size for this device. Nulls clear it back to the default. */
export function saveToteLabelSize(widthMm: number | null, heightMm: number | null): boolean {
    try {
        if (widthMm == null && heightMm == null) localStorage.removeItem(SIZE_KEY);
        else localStorage.setItem(SIZE_KEY, JSON.stringify({ widthMm, heightMm }));
        return true;
    } catch {
        return false;
    }
}

/** A tote row as the print modal's list wants it. The type goes in the slot
 *  column and doubles as the group, so the modal's pills filter by type. */
export function toteLabel(r: { tote_id: string; type?: string | null }): BayLabel {
    return { barcode: r.tote_id, label: r.tote_id, shelf: r.type ?? "", position: "", group: r.type ?? null };
}

export interface ToteLabelGeometry {
    W: number; H: number; padMm: number;
    barsW: number; barsH: number;
    /** Font size (mm) for the ID — fits both the text band and the width. */
    textFontMm: number;
}

// Arial Bold: capitals/digits are ~0.72 em tall and average ~0.68 em wide.
const CAP_HEIGHT = 0.72;
const CHAR_WIDTH = 0.68;

export function toteLabelGeometry(id: string, size?: Partial<Mm> | null): ToteLabelGeometry {
    const W = size?.widthMm && size.widthMm > 0 ? size.widthMm : TOTE_LABEL_BASE.widthMm;
    const H = size?.heightMm && size.heightMm > 0 ? size.heightMm : TOTE_LABEL_BASE.heightMm;
    const padMm = Math.max(1.5, Math.min(W, H) * 0.06);
    const innerW = W - padMm * 2;
    const innerH = H - padMm * 2;
    // Bars take ~55% of the height, the ID the rest (less a small gap).
    const barsH = innerH * 0.55;
    const textBand = innerH - barsH - padMm * 0.5;
    const chars = Math.max(1, id.trim().length);
    const byHeight = textBand / CAP_HEIGHT;
    const byWidth = innerW / (chars * CHAR_WIDTH);
    return { W, H, padMm, barsW: innerW, barsH, textFontMm: Math.max(2, Math.min(byHeight, byWidth)) };
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
// Safe inside a <script>: JSON quoting plus no way to close the tag.
const jsString = (s: string) => JSON.stringify(s).replace(/</g, "\\u003c");

/** One tote label as a print-ready HTML document (merged into a multi-page
 *  job by buildLabelSheet, like the racking strips). */
export function buildToteLabel(opts: { toteId: string; size?: Partial<Mm> | null }): string {
    const id = opts.toteId.trim();
    const g = toteLabelGeometry(id, opts.size);
    const mm = (n: number) => `${Math.round(n * 1000) / 1000}mm`;
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
@page { size: ${mm(g.W)} ${mm(g.H)}; margin: 0; }
body {
    width: ${mm(g.W)};
    height: ${mm(g.H)};
    padding: ${mm(g.padMm)};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    font-family: Arial, Helvetica, sans-serif;
}
svg { width: ${mm(g.barsW)} !important; height: ${mm(g.barsH)} !important; display: block; }
.id {
    font-size: ${mm(g.textFontMm)};
    /* 700, not 800 — 800 resolves to Arial Black on Windows and overflows. */
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    color: #000;
}
</style>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
</head>
<body>
<svg id="barcode"></svg>
<div class="id">${esc(id)}</div>
<script>
JsBarcode("#barcode", ${jsString(id)}, { format: "CODE128", width: 2, height: 90, displayValue: false, margin: 0 });
// Stretching vertically leaves the module widths (what the scanner reads) alone.
document.getElementById("barcode").setAttribute("preserveAspectRatio", "none");
<\/script>
</body>
</html>`;
}
